import type { DisplayQueueItem, Moment, QueueMessage } from '../types';

export function isSongItem(item: DisplayQueueItem): item is { kind: 'song'; data: QueueMessage } {
  return item.kind === 'song';
}

export function isMomentItem(item: DisplayQueueItem): item is { kind: 'moment'; data: Moment } {
  return item.kind === 'moment';
}

export function buildDisplayQueue(
  songs: QueueMessage[],
  moments: Moment[]
): DisplayQueueItem[] {
  const result: DisplayQueueItem[] = songs.map((s) => ({ kind: 'song', data: s }));
  const sorted = [...moments].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  for (const m of sorted) {
    const position = Math.max(0, m.position ?? 0);
    const idx = Math.min(position, result.length);
    result.splice(idx, 0, { kind: 'moment', data: m });
  }
  return result;
}

export function countSongs(items: DisplayQueueItem[]): number {
  return items.filter(isSongItem).length;
}

// Duração assumida quando um item da fila não tem duração conhecida
// (músicas adicionadas antes do campo existir, Spotify import, etc.).
export const FALLBACK_SONG_SECONDS = 210;

// Formata segundos para a estimativa de espera: "12 min", "1h 5min", "2h".
export function formatWaitTime(totalSeconds: number): string {
  const minutes = Math.ceil(totalSeconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours}h ${rest}min` : `${hours}h`;
}

export function countSongsBeforeIndex(items: DisplayQueueItem[], index: number): number {
  return items.slice(0, index).filter(isSongItem).length;
}

export function displayIndexToSongPosition(
  items: DisplayQueueItem[],
  displayIndex: number
): number {
  return countSongsBeforeIndex(items, displayIndex);
}

// The backend sends display queue items as flat objects (kind, id, userId, ...).
// The frontend uses a nested shape { kind: 'song', data: QueueMessage }.
// This helper converts the wire format safely.
export function normalizeDisplayQueue(items: any[]): DisplayQueueItem[] {
  return (items || []).map((item) => {
    if (!item || typeof item !== 'object') return null;
    if (item.kind === 'song' || (item.data && item.data.userId !== undefined)) {
      const data: QueueMessage = item.data || {
        id: item.id,
        userId: item.userId,
        userName: item.userName,
        avatarUrl: item.avatarUrl,
        content: item.content,
        metadata: item.metadata,
        timestamp: item.timestamp,
      };
      return { kind: 'song' as const, data };
    }
    if (item.kind === 'moment' || item.title !== undefined) {
      const data: Moment = item.data || {
        id: item.id,
        title: item.title,
        description: item.description,
        type: item.type,
        position: item.position,
        duration: item.duration,
      };
      return { kind: 'moment' as const, data };
    }
    return null;
  }).filter((item): item is DisplayQueueItem => item !== null);
}
